import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postFormData } from "../../../utility/Utility";
import Swal from "sweetalert2";

function AddCSV() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // File validation
  const validateFile = (file) => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      '.xlsx',
      '.xls',
      '.csv',
    ];
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!allowedTypes.some(type => file.type === type || file.name.toLowerCase().endsWith(type))) {
      toast.error("Please upload only Excel files (.xlsx, .xls)");
      return false;
    }
    
    if (file.size > maxSize) {
      toast.error("File size should be less than 10MB");
      return false;
    }
    
    return true;
  };

  // Auto upload function (without confirmation)
  const handleAutoUpload = useCallback(async (file) => {
    setLoading(true);
    setUploadProgress(0);

    let progressInterval;
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate upload progress
      progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = await postFormData("api/csv-leads/upload", formData);

      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (response?.success) {
        toast.success(response?.message);
        setTimeout(() => {
          navigate("/csv-lead-list");
        }, 2000);
      } else {
        toast.error(response?.message || "Failed to upload CSV file");
        // Reset file selection on error
        setSelectedFile(null);
        setUploadProgress(0);
      }
    } catch (error) {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      toast.error("Error uploading CSV file");
      console.error("Upload error:", error);
      // Reset file selection on error
      setSelectedFile(null);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Handle file selection and auto upload
  const handleFileSelect = useCallback(async (file) => {
    if (validateFile(file)) {
      setSelectedFile(file);
      
      // Automatically start upload
      await handleAutoUpload(file);
    }
  }, [handleAutoUpload]);

  // Drag handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  // File input change handler
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  // Remove selected file
  const removeFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setLoading(false);
  };


  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* <CustomLoader loading={loading} /> */}
      
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">CSV Lead Management</h4>
              <p className="fw-semibold my-2">Upload CSV File</p>
            </div>
            <div>
              <button
                className="btn btn-secondary me-2"
                onClick={() => navigate("/csv-lead-list")}
                disabled={loading}
              >
                <i className="mdi mdi-arrow-left me-1"></i>
                Back to List
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-8 mx-auto">
                      {/* Drag and Drop Area */}
                      <div
                        className={`border-2 border-dashed rounded-3 p-5 text-center position-relative ${
                          dragActive ? 'border-primary bg-light' : 'border-secondary'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        style={{ minHeight: '300px', cursor: 'pointer' }}
                      >
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleFileChange}
                          className="position-absolute w-100 h-100 opacity-0"
                          style={{ cursor: 'pointer', top: 0, left: 0 }}
                          disabled={loading}
                        />
                        
                        {!selectedFile ? (
                          <div className="d-flex flex-column align-items-center justify-content-center h-100">
                            <i className="mdi mdi-cloud-upload fs-1 text-muted mb-3"></i>
                            <h5 className="mb-3">
                              {dragActive ? "Drop your Excel file here" : "Drag & Drop Excel File"}
                            </h5>
                            <p className="text-muted mb-3">
                              or <span className="text-primary fw-semibold">browse</span> to choose a file
                            </p>
                            <small className="text-muted">
                              Supported formats: .xlsx, .xls (Max size: 10MB)
                            </small>
                          </div>
                        ) : (
                          <div className="d-flex flex-column align-items-center justify-content-center h-100">
                            <i className="mdi mdi-file-excel fs-1 text-success mb-3"></i>
                            <h6 className="mb-2">{selectedFile.name}</h6>
                            <p className="text-muted mb-3">
                              Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            
                            {uploadProgress > 0 && (
                              <div className="w-75 mb-3">
                                <div className="progress">
                                  <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                  >
                                    {uploadProgress}%
                                  </div>
                                </div>
                                <small className="text-muted mt-2">
                                  {loading ? "Uploading automatically..." : "Upload completed!"}
                                </small>
                              </div>
                            )}
                            
                            {loading && uploadProgress === 0 && (
                              <div className="text-center">
                                <div className="spinner-border text-primary mb-2" role="status">
                                  <span className="visually-hidden">Starting upload...</span>
                                </div>
                                <p className="text-muted mb-3">Starting automatic upload...</p>
                              </div>
                            )}
                            
                            <button
                              className="btn btn-outline-danger"
                              onClick={removeFile}
                              disabled={loading}
                            >
                              <i className="mdi mdi-delete me-1"></i>
                              {loading ? "Cancel Upload" : "Remove File"}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Instructions */}
                      <div className="mt-4">
                        <div className="alert alert-info">
                          <h6 className="alert-heading">
                            <i className="mdi mdi-information me-2"></i>
                            Upload Instructions
                          </h6>
                          <ul className="mb-0">
                            <li>Upload Excel files (.xlsx, .xls) containing lead data</li>
                            <li>Maximum file size: 10MB</li>
                            <li>Ensure your Excel file has proper column headers</li>
                            <li>Required columns: Name, Email, Phone, etc.</li>
                            <li>Invalid rows will be skipped during import</li>
                          </ul>
                        </div>
                      </div>

                      {/* Sample Format Download */}
                      <div className="mt-3 text-center">
                        <button className="btn btn-outline-primary">
                          <i className="mdi mdi-download me-1"></i>
                          Download Sample Format
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddCSV;